// Note: This apiClient is now context-aware
// Use the useApiClient hook from components for React Context integration

// API base URL logic
const getApiBaseUrl = () => {
  // 1. Prioritize the explicit API URL from .env
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 2. Fallbacks based on environment tag
  const env = process.env.REACT_APP_CURRENT_ENV;

  if (env === 'local') {
    return 'http://localhost:3000';
  }

  // Default to production URL if nothing else matches
  return 'https://apideltaops.thecbt.live';
};

// Base API client with authentication
class ApiClient {
  constructor(authMethods = null) {
    this.baseURL = getApiBaseUrl();
    this.authMethods = authMethods; // { getAuthHeaders, logout }
  }

  // Make authenticated request
  async request(endpoint, options = {}) {
    try {
      // Get auth headers from provided methods or fallback
      let authHeaders = {};
      if (this.authMethods && this.authMethods.getAuthHeaders) {
        authHeaders = await this.authMethods.getAuthHeaders();
      }

      // Merge headers
      const headers = {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers
      };

      // Make request
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers
      });

      // Handle unauthorized responses
      if (response.status === 401) {
        console.log('Unauthorized request, clearing session...');
        if (this.authMethods && this.authMethods.logout) {
          await this.authMethods.logout();
        }
        throw new Error('Authentication required');
      }

      // Parse response
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // GET request
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // POST request
  async post(endpoint, body) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // PUT request
  async put(endpoint, body) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // PATCH request
  async patch(endpoint, body) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }
}

// Create base singleton instance (for backward compatibility)
const apiClient = new ApiClient();

// Create context-aware instance factory
export const createContextAwareApiClient = (authMethods) => {
  return new ApiClient(authMethods);
};

export default apiClient;
